import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Project } from '../../types';
import { dossierAPI, analysisAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { LockBar } from '../dossier/LockBar';
import { useSectionClear } from '../dossier/useSectionClear';
import { deriveAvatar, genderNL, geslachtUitNaam, normGender as normGeslacht } from './personaAvatars';
import { PersonasIcon } from '../common/Icons/Icons';
import { PARTICIPATIE, PARTICIPATIE_COLORS, PARTICIPATIE_TEXT, normalizeParticipatie } from '../../constants/participatie';
import { BRAND, faseKleur } from '../../styles/brand';
import { FaseTag } from '../flow/FaseTag';

interface PersonasFaseModuleProps {
  project: Project;
}

interface Persona {
  id: string;
  naam: string;
  groep: string;
  geslacht: string;
  leeftijd: string;
  stakeholderId: string;
  stakeholderNaam: string;
  afbeelding: string;
  belangen: string;
  zorgen: string;
  verwachtingen: string;
  informatiebehoefte: string;
  voorkennis: string;
  communicatievoorkeuren: string;
  mediagebruik: string;
  taalniveau: string;
  huidigGedrag: string;
  gewenstGedrag: string;
  rol: string;
  participatieniveau: string; // Factor C-trede
  ringVanInvloed: string;     // niet meer in beeld (feedback Leene sept 2026); bewaard voor bestaande dossiers
  gebaseerdOp: string;        // namen van de stakeholders waarop de persona is gebaseerd, '; '-gescheiden
  [k: string]: string;
}

const splitNamen = (v?: string): string[] => String(v || '').split(';').map((x) => x.trim()).filter(Boolean);
const joinNamen = (namen: string[]): string => Array.from(new Set(namen)).join('; ');

const FIELD_ICONS: Record<string, React.ReactNode> = {
  belangen: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>),
  zorgen: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>),
  verwachtingen: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>),
  informatiebehoefte: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>),
  voorkennis: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>),
  communicatievoorkeuren: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>),
  mediagebruik: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>),
  taalniveau: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="17" y1="10" x2="3" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="17" y1="18" x2="3" y2="18" /></svg>),
  huidigGedrag: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>),
  gewenstGedrag: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>),
  rol: (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><polyline points="17 11 19 13 23 9" /></svg>),
};

const PERSONA_FIELDS: { key: keyof Persona; label: string }[] = [
  { key: 'belangen', label: 'Belangen' },
  { key: 'zorgen', label: 'Zorgen' },
  { key: 'verwachtingen', label: 'Verwachtingen' },
  { key: 'informatiebehoefte', label: 'Informatiebehoefte' },
  { key: 'voorkennis', label: 'Voorkennis' },
  { key: 'communicatievoorkeuren', label: 'Communicatievoorkeuren' },
  { key: 'mediagebruik', label: 'Mediagebruik' },
  { key: 'taalniveau', label: 'Taalniveau' },
  { key: 'huidigGedrag', label: 'Huidig gedrag' },
  { key: 'gewenstGedrag', label: 'Gewenst gedrag' },
  { key: 'rol', label: 'Rol in het project' },
];

function emptyPersona(id: string): Persona {
  return {
    id, naam: '', groep: '', geslacht: '', leeftijd: '', stakeholderId: '', stakeholderNaam: '', afbeelding: '', belangen: '', zorgen: '', verwachtingen: '', informatiebehoefte: '',
    voorkennis: '', communicatievoorkeuren: '', mediagebruik: '', taalniveau: '', huidigGedrag: '',
    gewenstGedrag: '', rol: '', participatieniveau: '', ringVanInvloed: '', gebaseerdOp: '',
  };
}

/** Bestaande dossiers bijwerken: oude ladder → Factor C, oude 1-op-1-koppeling → gebaseerdOp. */
function normalizePersona(p: Persona): Persona {
  return {
    ...p,
    participatieniveau: normalizeParticipatie(p.participatieniveau),
    gebaseerdOp: p.gebaseerdOp || p.stakeholderNaam || '',
    // Leeg geslacht aanvullen uit de voornaam; anders kiest de fototool er een die niet past.
    geslacht: p.geslacht || genderNL(geslachtUitNaam(p.naam)),
  };
}

/** Unieke, niet-lege teksten in volgorde onder elkaar (hoofdlettergebruik telt niet als verschil). */
function combineTeksten(waarden: string[], scheiding = '\n\n'): string {
  const gezien = new Set<string>();
  const uniek: string[] = [];
  waarden.forEach((w) => {
    const t = String(w || '').trim();
    if (t && !gezien.has(t.toLowerCase())) { gezien.add(t.toLowerCase()); uniek.push(t); }
  });
  return uniek.join(scheiding);
}

/** Voorstel bij samenvoegen: teksten gecombineerd, profiel van de eerste, hoogste participatietrede, herkomst samen. */
function mergePersonas(bronnen: Persona[], id: string): Persona {
  const eerste = bronnen[0];
  const merged: Persona = {
    ...emptyPersona(id),
    naam: eerste.naam,
    groep: combineTeksten(bronnen.map((b) => b.groep), ' / '),
    geslacht: eerste.geslacht,
    leeftijd: eerste.leeftijd,
    gebaseerdOp: joinNamen(bronnen.flatMap((b) => splitNamen(b.gebaseerdOp))),
    ringVanInvloed: bronnen.map((b) => b.ringVanInvloed).find(Boolean) || '',
  };
  PERSONA_FIELDS.forEach((f) => { merged[f.key] = combineTeksten(bronnen.map((b) => b[f.key])); });
  const hoogste = Math.max(...bronnen.map((b) => PARTICIPATIE.indexOf(b.participatieniveau)));
  merged.participatieniveau = hoogste >= 0 ? PARTICIPATIE[hoogste] : (bronnen.map((b) => b.participatieniveau).find(Boolean) || '');
  merged.afbeelding = deriveAvatar(merged);
  return merged;
}

function pickStyle(active: boolean): React.CSSProperties {
  return { padding: '3px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, border: `1px solid ${active ? BRAND.groen : BRAND.rand}`, background: active ? BRAND.groen : '#fff', color: active ? '#fff' : '#4b5563', cursor: 'pointer', whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' };
}

function initials(naam: string): string {
  // Leeftijd tussen haakjes telt niet mee: "Jongere Sam (19)" → "JS".
  const parts = naam.replace(/\([^)]*\)/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

let idSeq = 0;
function newId(): string {
  idSeq += 1;
  try { if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) return (crypto as any).randomUUID(); } catch (e) { /* fallback */ }
  return `p-${idSeq}-${Math.round(Math.random() * 1e6)}`;
}

const labelStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, color: '#1a1a2e', marginBottom: 4 };
function inputStyle(disabled: boolean): React.CSSProperties {
  return { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid #d8dce4', fontSize: 13.5, fontFamily: 'inherit', color: '#20242e', background: disabled ? '#f6f7f9' : '#fff', boxSizing: 'border-box' };
}
function areaStyle(disabled: boolean): React.CSSProperties {
  return { ...inputStyle(disabled), minHeight: 58, resize: 'vertical', lineHeight: 1.5 };
}

/** Avatar met fallback naar initialen. */
const Avatar: React.FC<{ p: Persona; size: number; ring?: string }> = ({ p, size, ring }) => {
  const src = p.afbeelding || deriveAvatar(p);
  if (src) {
    return <img src={src} alt={p.naam || 'Persona'} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, boxShadow: ring ? `0 0 0 3px ${ring}` : 'none' }} />;
  }
  return (
    <span style={{ display: 'inline-flex', width: size, height: size, borderRadius: '50%', background: '#eef1f6', color: '#33417a', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.32, fontWeight: 700, flexShrink: 0 }}>
      {initials(p.naam)}
    </span>
  );
};

/** Bewerkbaar formulier voor één persona (detail- en aanmaakpagina). */
const PersonaForm: React.FC<{ p: Persona; locked: boolean; stakeholderOpties: string[]; onChange: (k: keyof Persona, v: string) => void }> = ({ p, locked, stakeholderOpties, onChange }) => {
  const partIdx = PARTICIPATIE.indexOf(p.participatieniveau);
  const gekozen = splitNamen(p.gebaseerdOp);
  const [toonStakeholders, setToonStakeholders] = useState(false); // herkomst staat standaard ingeklapt
  // Ook namen tonen die (nog) niet in de huidige stakeholderlijst staan, zodat je ze kunt weghalen.
  const opties = locked ? gekozen : Array.from(new Set([...stakeholderOpties, ...gekozen]));
  const toggleStakeholder = (naam: string) =>
    onChange('gebaseerdOp', joinNamen(gekozen.includes(naam) ? gekozen.filter((n) => n !== naam) : [...gekozen, naam]));
  return (
    <div>
      {/* Foto (automatisch) + geslacht/leeftijd */}
      <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ borderRadius: '50%', border: '2px solid #e5e7eb', flexShrink: 0, display: 'inline-flex' }}><Avatar p={p} size={76} /></span>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, flex: 1, minWidth: 220 }}>
          <div>
            <label style={labelStyle}>Geslacht</label>
            <select value={genderNL(p.geslacht)} disabled={locked} onChange={(e) => onChange('geslacht', e.target.value)} style={inputStyle(locked)}>
              <option value="">—</option>
              <option value="vrouw">Vrouw</option>
              <option value="man">Man</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Leeftijd</label>
            <input type="number" min={0} max={120} value={p.leeftijd} disabled={locked} onChange={(e) => onChange('leeftijd', e.target.value)} style={inputStyle(locked)} placeholder="bijv. 68" />
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#8a90a0', marginBottom: 16 }}>
        De foto wordt automatisch gekozen op basis van geslacht en leeftijd.{!normGeslacht(p.geslacht) && ' Kies een geslacht voor een passende foto.'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Naam</label>
          <input value={p.naam} disabled={locked} onChange={(e) => onChange('naam', e.target.value)} style={inputStyle(locked)} placeholder="bijv. Bezorgde bewoner Anja (68)" />
        </div>
        <div>
          <label style={labelStyle}>Vertegenwoordigde groep</label>
          <input value={p.groep} disabled={locked} onChange={(e) => onChange('groep', e.target.value)} style={inputStyle(locked)} placeholder="bijv. Oudere bewoners Dorpsstraat" />
        </div>
      </div>

      {/* Herkomst: op welke stakeholder(s) is deze persona gebaseerd. Ingeklapt, zodat de lijst de rest van het formulier niet in de weg staat. */}
      <div style={{ marginTop: 12, border: `1px solid ${BRAND.rand}`, borderRadius: 10, background: '#fff' }}>
        <button type="button" onClick={() => setToonStakeholders((open) => !open)} aria-expanded={toonStakeholders}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>
              Gebaseerd op stakeholders{gekozen.length > 0 && <span style={{ fontWeight: 500, color: '#8a90a0' }}> ({gekozen.length})</span>}
            </span>
            {!toonStakeholders && (
              <span style={{ display: 'block', fontSize: 12.5, color: '#8a90a0', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {gekozen.length ? gekozen.join(', ') : (locked ? 'Niet vastgelegd.' : 'Nog geen stakeholders gekozen.')}
              </span>
            )}
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: toonStakeholders ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}><polyline points="6 9 12 15 18 9" /></svg>
        </button>
        {toonStakeholders && (
          <div style={{ padding: '0 12px 12px' }}>
            <div style={{ fontSize: 12, color: '#8a90a0', marginBottom: 8 }}>
              {locked ? 'Uit de stakeholderanalyse (Fase 2).' : 'Uit de stakeholderanalyse (Fase 2). Klik een stakeholder aan of uit.'}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {opties.map((naam) => {
                const actief = gekozen.includes(naam);
                return (
                  <button key={naam} type="button" disabled={locked} onClick={() => toggleStakeholder(naam)} aria-pressed={actief} title={naam}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: 'min(100%, 320px)', padding: '5px 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, border: `1px solid ${actief ? BRAND.groen : BRAND.rand}`, background: actief ? BRAND.groen : '#fff', color: actief ? '#fff' : '#4b5563', cursor: locked ? 'default' : 'pointer' }}>
                    {actief && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12" /></svg>}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{naam}</span>
                  </button>
                );
              })}
              {opties.length === 0 && (
                <span style={{ fontSize: 12.5, color: '#8a90a0' }}>{locked ? 'Niet vastgelegd.' : 'Nog geen stakeholders in de omgevingsanalyse.'}</span>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
        {PERSONA_FIELDS.map((f) => (
          <div key={String(f.key)}>
            <label style={labelStyle}>
              <span style={{ color: '#9aa1b0', display: 'inline-flex' }}>{FIELD_ICONS[f.key as string]}</span>
              {f.label}
            </label>
            <textarea value={p[f.key]} disabled={locked} onChange={(e) => onChange(f.key, e.target.value)} style={areaStyle(locked)} />
          </div>
        ))}
      </div>

      {/* Participatie-indeling */}
      <div style={{ marginTop: 14, padding: 16, borderRadius: 10, background: '#fafbfc', border: '1px solid #eef0f4' }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 14 }}>Participatie-indeling</div>
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Participatieniveau (Factor C)</label>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
            {PARTICIPATIE.map((level, i) => (
              <React.Fragment key={level}>
                {i > 0 && <div style={{ flex: 1, height: 2, background: partIdx >= i ? BRAND.groen : '#dfe2e8' }} />}
                <button type="button" title={level} aria-label={level} aria-pressed={i === partIdx} disabled={locked} onClick={() => onChange('participatieniveau', level)}
                  style={{ width: i === partIdx ? 16 : 11, height: i === partIdx ? 16 : 11, borderRadius: '50%', border: `2px solid ${partIdx >= i ? BRAND.groen : '#c7cbd6'}`, background: partIdx >= i ? BRAND.groen : '#fff', padding: 0, flexShrink: 0, cursor: locked ? 'default' : 'pointer' }} />
              </React.Fragment>
            ))}
          </div>
          <div style={{ position: 'relative', height: 16, marginTop: 7 }}>
            {PARTICIPATIE.map((level, i) => {
              const last = PARTICIPATIE.length - 1;
              return (
                <span key={level} style={{ position: 'absolute', left: `${(i / last) * 100}%`, transform: `translateX(${i === 0 ? '0' : i === last ? '-100%' : '-50%'})`, whiteSpace: 'nowrap', fontSize: 11, color: level === p.participatieniveau ? BRAND.groen : '#9aa1b0', fontWeight: level === p.participatieniveau ? 700 : 500 }}>{level}</span>
              );
            })}
          </div>
          {p.participatieniveau && partIdx < 0 && (
            <div style={{ fontSize: 12, color: '#8a90a0', marginTop: 6 }}>Huidige waarde: {p.participatieniveau}. Kies een trede om bij te werken.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const pageWrap: React.CSSProperties = { padding: '32px', maxWidth: 1000, margin: '0 auto' };
const backBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 18, padding: '6px 10px 6px 6px', borderRadius: 8, border: 'none', background: 'transparent', color: '#6b7280', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' };

export const PersonasFaseModule: React.FC<PersonasFaseModuleProps> = ({ project }) => {
  const toast = useToast();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [status, setStatus] = useState<'open' | 'locked'>('open');
  const [lockedAt, setLockedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [stakeholders, setStakeholders] = useState<any[]>([]); // gevalideerde/opgeslagen stakeholders uit Fase 2
  const [signals, setSignals] = useState<{ gebied: boolean; media: boolean }>({ gebied: false, media: false }); // welke bronnen voeden de AI
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const personaParam = searchParams.get('persona');
  const isCreate = personaParam === 'nieuw';
  const [draft, setDraft] = useState<Persona | null>(null); // aanmaakpagina
  const openEdit = (id: string) => setSearchParams({ persona: id });
  const closeDetail = () => setSearchParams({});
  // Samenvoegen: persona's selecteren op het overzicht → samenvoegpagina (?samenvoegen=id1,id2)
  const samenvoegenParam = searchParams.get('samenvoegen');
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [mergeDraft, setMergeDraft] = useState<{ ids: string; persona: Persona } | null>(null);

  const locked = status === 'locked';

  const loadDossier = useCallback(async () => {
    setLoading(true);
    try {
      // Gebiedskaart: in het dossier overgenomen, of anders rechtstreeks aanwezig (de AI leest beide).
      const [res, area] = await Promise.all([
        dossierAPI.getByProject(project.id),
        analysisAPI.getArea(project.id).catch(() => null),
      ]);
      const oa = (res?.dossier?.sections?.omgevingsanalyse?.content) || {};
      setStakeholders(Array.isArray(oa.stakeholdersList) ? oa.stakeholdersList : []);
      const gebied = !!oa.gebiedskaartSnapshot || !!(area && (area.demographics || area.gebiedsprofiel));
      setSignals({ gebied, media: !!oa.nieuwsSentiment || !!oa.coostoSentiment });
      const section = res?.dossier?.sections?.personas;
      if (section) {
        const list: Persona[] = Array.isArray(section.content?.personas) ? section.content.personas : [];
        setPersonas(list.map((p) => {
          const base = normalizePersona({ ...emptyPersona(p.id || newId()), ...p });
          return { ...base, afbeelding: deriveAvatar(base) };
        }));
        setStatus(section.status === 'locked' ? 'locked' : 'open');
        setLockedAt(section.lockedAt || null);
      }
    } catch (e) {
      // dossier wordt server-side aangemaakt zodra beschikbaar
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { loadDossier(); }, [loadDossier]);

  // Beheerders kunnen deze fase leegmaken om in een testsessie opnieuw te vullen.
  const { canClear, clearing, doClear } = useSectionClear(project.id, project.name, 'personas', "Persona's", { personas: [] }, loadDossier);

  // Houd het aanmaak-concept in sync met de URL (?persona=nieuw), zodat browser-back werkt.
  useEffect(() => {
    if (isCreate) setDraft((d) => d || (() => { const b = emptyPersona(newId()); return { ...b, afbeelding: deriveAvatar(b) }; })());
    else setDraft(null);
  }, [isCreate]);

  const updatePersona = (id: string, key: keyof Persona, value: string) => {
    setPersonas((list) => list.map((p) => {
      if (p.id !== id) return p;
      const next = { ...p, [key]: value };
      if (key === 'geslacht' || key === 'leeftijd' || key === 'naam') next.afbeelding = deriveAvatar(next);
      return next;
    }));
  };
  const removePersona = async (id: string) => {
    const next = personas.filter((p) => p.id !== id);
    setPersonas(next);
    closeDetail();
    try {
      await persist(next);
    } catch (e: any) {
      toast.error('Verwijderen niet opgeslagen', e?.response?.data?.error || 'Probeer het opnieuw.');
      loadDossier(); // herstel de waarheid uit de backend
    }
  };
  const clearAllPersonas = async () => {
    if (personas.length === 0) return;
    if (!window.confirm(`Weet je zeker dat je alle ${personas.length} persona's wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`)) return;
    setPersonas([]);
    closeDetail();
    try {
      await persist([]);
      toast.success("Persona's verwijderd", 'Je kunt nu opnieuw genereren.');
    } catch (e: any) {
      toast.error('Verwijderen niet opgeslagen', e?.response?.data?.error || 'Probeer het opnieuw.');
      loadDossier();
    }
  };

  const openCreate = () => { const base = emptyPersona(newId()); setDraft({ ...base, afbeelding: deriveAvatar(base) }); setSearchParams({ persona: 'nieuw' }); };
  const changeDraft = (k: keyof Persona, v: string) => setDraft((d) => {
    if (!d) return d;
    const next = { ...d, [k]: v };
    if (k === 'geslacht' || k === 'leeftijd' || k === 'naam') next.afbeelding = deriveAvatar(next);
    return next;
  });
  const saveDraftPersona = () => {
    if (!draft) return;
    if (!draft.naam.trim()) { toast.error('Naam ontbreekt', 'Geef de persona een naam.'); return; }
    const added = draft;
    setPersonas((list) => [...list, added]);
    setDraft(null);
    setSearchParams({ persona: added.id }); // ga door op de detailpagina van de nieuwe persona
    toast.success('Persona toegevoegd', 'Verrijk de persona gerust verder.');
  };

  const persist = async (list: Persona[]) => dossierAPI.saveSection(project.id, 'personas', { personas: list });

  const saveDraft = async () => {
    setSaving(true);
    try {
      await persist(personas);
      toast.success('Concept opgeslagen', "De persona's zijn als concept vastgelegd.");
    } catch (e: any) {
      toast.error('Opslaan mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setSaving(false); }
  };

  const runAssist = async () => {
    setAiLoading(true);
    try {
      // De stakeholderanalyse gaat mee als INPUT (welke groepen mensen doen ertoe) — niet 1-op-1.
      const res = await dossierAPI.assistPersonas(project.id, stakeholders);
      let aiList: Persona[] = (res?.personas || []).map((p: any) => {
        const base = normalizePersona({ ...emptyPersona(newId()), ...p, id: newId() });
        return { ...base, afbeelding: deriveAvatar(base) };
      });
      // Dedup op naam: voorkom dubbele concept-persona's bij opnieuw genereren.
      const bestaandeNamen = new Set(personas.map((p) => p.naam.trim().toLowerCase()).filter(Boolean));
      aiList = aiList.filter((p) => !(p.naam.trim() && bestaandeNamen.has(p.naam.trim().toLowerCase())));
      if (aiList.length === 0) {
        toast.error('Geen nieuwe persona’s', 'De AI leverde geen nieuwe concept-persona’s. Vul eerst de omgevingsanalyse verder in, of pas de prompt aan bij Beheer › AI-prompts.');
      } else {
        const merged = [...personas, ...aiList];
        setPersonas(merged);
        try { await persist(merged); } catch (e) { /* blijft in concept; expliciet opslaan blijft mogelijk */ }
        toast.success('Concept-persona’s toegevoegd', `${aiList.length} persona('s) toegevoegd op basis van je omgevingsanalyse. Klik op een persona om te verrijken.`);
      }
    } catch (e: any) {
      toast.error('AI-ondersteuning mislukt', e?.response?.data?.error || 'Probeer het later opnieuw.');
    } finally { setAiLoading(false); }
  };

  useEffect(() => {
    if (!samenvoegenParam) { setMergeDraft(null); return; }
    setMergeDraft((cur) => {
      if (cur && cur.ids === samenvoegenParam) return cur;
      const bronnen = samenvoegenParam.split(',').map((id) => personas.find((p) => p.id === id)).filter(Boolean) as Persona[];
      return bronnen.length >= 2 ? { ids: samenvoegenParam, persona: mergePersonas(bronnen, newId()) } : null;
    });
  }, [samenvoegenParam, personas]);

  const toggleSelect = (id: string) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const stopSelect = () => { setSelectMode(false); setSelected([]); };
  const startMerge = () => { if (selected.length >= 2) setSearchParams({ samenvoegen: selected.join(',') }); };
  const changeMerge = (k: keyof Persona, v: string) => setMergeDraft((m) => {
    if (!m) return m;
    const next = { ...m.persona, [k]: v };
    if (k === 'geslacht' || k === 'leeftijd' || k === 'naam') next.afbeelding = deriveAvatar(next);
    return { ...m, persona: next };
  });
  const profielVan = (b: Persona) => setMergeDraft((m) => {
    if (!m) return m;
    const next = { ...m.persona, geslacht: b.geslacht, leeftijd: b.leeftijd };
    return { ...m, persona: { ...next, afbeelding: deriveAvatar(next) } };
  });
  const confirmMerge = async () => {
    if (!mergeDraft || locked) return;
    const ids = mergeDraft.ids.split(',');
    const merged = { ...mergeDraft.persona, naam: mergeDraft.persona.naam.trim() };
    if (!merged.naam) { toast.error('Naam ontbreekt', 'Geef de samengevoegde persona een naam.'); return; }
    // De nieuwe persona komt op de plek van de eerste samengevoegde persona.
    const eersteIdx = personas.findIndex((p) => ids.includes(p.id));
    const rest = personas.filter((p) => !ids.includes(p.id));
    const insertAt = personas.slice(0, Math.max(eersteIdx, 0)).filter((p) => !ids.includes(p.id)).length;
    const next = [...rest.slice(0, insertAt), merged, ...rest.slice(insertAt)];
    setPersonas(next);
    stopSelect();
    setMergeDraft(null);
    setSearchParams({ persona: merged.id });
    try {
      await persist(next);
      toast.success("Persona's samengevoegd", `${ids.length} persona's zijn nu één doelgroep. Verrijk de persona gerust verder.`);
    } catch (e: any) {
      toast.error('Samenvoegen niet opgeslagen', e?.response?.data?.error || 'Probeer het opnieuw.');
      loadDossier();
    }
  };

  const canLock = personas.length > 0 && personas.some((p) => p.naam.trim());

  const doLock = async () => {
    setLocking(true);
    try {
      const res = await dossierAPI.lockSection(project.id, 'personas', { personas });
      setStatus('locked');
      setLockedAt(res?.section?.lockedAt || new Date().toISOString());
      toast.success('Fase gevalideerd', "De persona's zijn vastgelegd en vormen nu het virtuele communicatiepanel.");
    } catch (e: any) {
      toast.error('Valideren mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setLocking(false); }
  };

  const doUnlock = async () => {
    setLocking(true);
    try {
      await dossierAPI.unlockSection(project.id, 'personas');
      setStatus('open');
      toast.success('Fase heropend', "Je kunt de persona's nu aanpassen en opnieuw valideren.");
    } catch (e: any) {
      toast.error('Heropenen mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setLocking(false); }
  };

  const editing = personaParam && !isCreate ? (personas.find((p) => p.id === personaParam) || null) : null;
  const stakeholderOpties: string[] = stakeholders.map((sh) => String(sh?.naam || '').trim()).filter(Boolean);

  if (loading) return <div style={{ padding: 32, color: '#6b7280' }}>Projectdossier laden…</div>;

  // ---- Volledige pagina: persona's samenvoegen ----
  if (mergeDraft && !locked) {
    const bronnen = mergeDraft.ids.split(',').map((id) => personas.find((p) => p.id === id)).filter(Boolean) as Persona[];
    const m = mergeDraft.persona;
    const keuzes = (k: keyof Persona, alles: string) => {
      const metInhoud = bronnen.filter((b) => String(b[k] || '').trim());
      if (new Set(metInhoud.map((b) => String(b[k]).trim().toLowerCase())).size < 2) return null;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', margin: '2px 0 6px' }}>
          <span style={{ fontSize: 11.5, color: '#8a90a0' }}>Neem:</span>
          <button type="button" onClick={() => changeMerge(k, alles)} style={pickStyle(m[k] === alles)}>Alles</button>
          {metInhoud.map((b) => (
            <button key={b.id} type="button" onClick={() => changeMerge(k, String(b[k]).trim())} style={pickStyle(m[k] === String(b[k]).trim())} title={b.naam}>{b.naam || 'Naamloos'}</button>
          ))}
        </div>
      );
    };
    const partIdx = PARTICIPATIE.indexOf(m.participatieniveau);
    return (
      <div style={pageWrap}>
        <button onClick={closeDetail} style={backBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          Terug naar persona's
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex' }}>
            {bronnen.map((b, i) => (
              <span key={b.id} style={{ marginLeft: i === 0 ? 0 : -12, borderRadius: '50%', boxShadow: '0 0 0 3px #fff' }}><Avatar p={b} size={44} /></span>
            ))}
          </div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1a1a2e' }}>Persona's samenvoegen</h1>
        </div>
        <p style={{ margin: '0 0 18px', fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>
          {bronnen.map((b) => b.naam || 'Naamloze persona').join(', ')} worden één doelgroep. De teksten staan al samengevoegd;
          pas ze aan of kies per onderdeel één versie. De oorspronkelijke persona's worden vervangen door deze ene persona.
        </p>

        <div style={{ background: '#fff', border: '1px solid #ececf1', borderRadius: 16, padding: 24 }}>
          <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 14 }}>
            <span style={{ borderRadius: '50%', border: '2px solid #e5e7eb', flexShrink: 0, display: 'inline-flex' }}><Avatar p={m} size={76} /></span>
            <div style={{ flex: 1, minWidth: 240, display: 'grid', gap: 12 }}>
              <div>
                <label style={labelStyle}>Naam</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', margin: '2px 0 6px' }}>
                  <span style={{ fontSize: 11.5, color: '#8a90a0' }}>Neem:</span>
                  {bronnen.filter((b) => b.naam.trim()).map((b) => (
                    <button key={b.id} type="button" onClick={() => changeMerge('naam', b.naam)} style={pickStyle(m.naam === b.naam)}>{b.naam}</button>
                  ))}
                </div>
                <input value={m.naam} onChange={(e) => changeMerge('naam', e.target.value)} style={inputStyle(false)} placeholder="Naam van de samengevoegde persona" />
              </div>
              <div>
                <label style={labelStyle}>Vertegenwoordigde groep</label>
                {keuzes('groep', combineTeksten(bronnen.map((b) => b.groep), ' / '))}
                <input value={m.groep} onChange={(e) => changeMerge('groep', e.target.value)} style={inputStyle(false)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: '#1a1a2e', marginRight: 4 }}>Foto, geslacht en leeftijd van:</span>
                {bronnen.map((b) => (
                  <button key={b.id} type="button" onClick={() => profielVan(b)} style={pickStyle(m.geslacht === b.geslacht && m.leeftijd === b.leeftijd)}>{b.naam || 'Naamloos'}</button>
                ))}
              </div>
            </div>
          </div>

          {m.gebaseerdOp && (
            <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 14 }}>
              <strong style={{ color: '#1a1a2e' }}>Gebaseerd op stakeholders:</strong> {splitNamen(m.gebaseerdOp).join(', ')}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Participatieniveau (Factor C)</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {PARTICIPATIE.map((level, i) => (
                <button key={level} type="button" aria-pressed={i === partIdx} onClick={() => changeMerge('participatieniveau', level)}
                  style={{ padding: '6px 13px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, border: `1px solid ${i === partIdx ? BRAND.groen : BRAND.rand}`, background: i === partIdx ? BRAND.groen : '#fff', color: i === partIdx ? '#fff' : '#4b5563', cursor: 'pointer' }}>
                  {level}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
            {PERSONA_FIELDS.map((f) => (
              <div key={String(f.key)}>
                <label style={labelStyle}>
                  <span style={{ color: '#9aa1b0', display: 'inline-flex' }}>{FIELD_ICONS[f.key as string]}</span>
                  {f.label}
                </label>
                {keuzes(f.key, combineTeksten(bronnen.map((b) => b[f.key])))}
                <textarea value={m[f.key]} onChange={(e) => changeMerge(f.key, e.target.value)} style={{ ...areaStyle(false), minHeight: 84 }} />
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
          <button onClick={closeDetail} style={{ padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}>Annuleren</button>
          <button onClick={confirmMerge} style={{ padding: '10px 22px', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none', background: BRAND.groen, color: '#fff', cursor: 'pointer' }}>Samenvoegen tot één persona</button>
        </div>
      </div>
    );
  }

  // ---- Volledige pagina: persona bewerken ----
  if (editing) {
    return (
      <div style={pageWrap}>
        <button onClick={closeDetail} style={backBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          Terug naar persona's
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
          <Avatar p={editing} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1a1a2e' }}>{editing.naam || 'Naamloze persona'}</h1>
            {editing.groep && <div style={{ fontSize: 14, color: '#6b7280', marginTop: 2 }}>{editing.groep}</div>}
            {editing.gebaseerdOp && <div style={{ fontSize: 13, color: BRAND.groenMuted, marginTop: 3, fontWeight: 600 }}>Gebaseerd op: {splitNamen(editing.gebaseerdOp).join(', ')}</div>}
          </div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #ececf1', borderRadius: 16, padding: 24 }}>
          <PersonaForm p={editing} locked={locked} stakeholderOpties={stakeholderOpties} onChange={(k, v) => updatePersona(editing.id, k, v)} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 20 }}>
          {!locked
            ? <button onClick={() => removePersona(editing.id)} style={{ padding: '10px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: '1px solid #f0c9c9', background: '#fff', color: '#b91c1c', cursor: 'pointer' }}>Persona verwijderen</button>
            : <span />}
          <button onClick={closeDetail} style={{ padding: '10px 22px', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none', background: '#1e3a2f', color: '#fff', cursor: 'pointer' }}>Klaar</button>
        </div>
      </div>
    );
  }

  // ---- Volledige pagina: nieuwe persona ----
  if (isCreate && draft) {
    return (
      <div style={pageWrap}>
        <button onClick={closeDetail} style={backBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          Terug naar persona's
        </button>
        <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 700, color: '#1a1a2e' }}>Nieuwe persona</h1>
        <p style={{ margin: '0 0 18px', fontSize: 14, color: '#6b7280' }}>Vul de gegevens in; de foto wordt automatisch gekozen op basis van geslacht en leeftijd.</p>
        <div style={{ background: '#fff', border: '1px solid #ececf1', borderRadius: 16, padding: 24 }}>
          <PersonaForm p={draft} locked={false} stakeholderOpties={stakeholderOpties} onChange={changeDraft} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
          <button onClick={closeDetail} style={{ padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}>Annuleren</button>
          <button onClick={saveDraftPersona} style={{ padding: '10px 22px', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none', background: '#1e3a2f', color: '#fff', cursor: 'pointer' }}>Persona toevoegen</button>
        </div>
      </div>
    );
  }

  // ---- Overzicht ----
  return (
    <div style={pageWrap}>
      <FaseTag nr={3} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ display: 'inline-flex', width: 36, height: 36, borderRadius: 10, background: faseKleur(3).soft, color: BRAND.groen, alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <PersonasIcon size={20} />
        </span>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: BRAND.groen, letterSpacing: '-0.3px' }}>Persona's</h1>
      </div>
      <p style={{ margin: '0 0 20px', color: '#555', fontSize: 15, lineHeight: 1.6 }}>
        Ontwikkel persona's die de belangrijkste <strong>doelgroepen in de omgeving</strong> vertegenwoordigen —
        de mensen die je met communicatie wilt bereiken. Een stakeholder is geen persona; de stakeholderanalyse
        dient als input. Klik op een persona om te beoordelen en verrijken. Na validatie vormen ze het
        <strong> virtuele communicatiepanel</strong> voor de pretest van boodschappen.
      </p>

      {/* AI-ondersteuning */}
      <div style={{ padding: 20, marginBottom: 20, borderRadius: 12, background: faseKleur(3).soft, border: `1px solid ${faseKleur(3).border}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...labelStyle, marginBottom: 2 }}>AI-ondersteuning</div>
            <div style={{ fontSize: 12.5, color: '#8a90a0' }}>Genereer tot 12 concept-persona's van de relevante doelgroepen in de omgeving, met per persona de stakeholders waarop die is gebaseerd. De stakeholderanalyse, gebiedsdata en het mediasentiment dienen als input. Jij beoordeelt en verrijkt.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {!locked && personas.length > 0 && (
              <button onClick={clearAllPersonas} disabled={aiLoading}
                title="Verwijder alle persona's zodat je opnieuw kunt genereren"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: '1px solid #f3c9c9', background: '#fff', color: '#b91c1c', cursor: aiLoading ? 'default' : 'pointer', opacity: aiLoading ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                Alles verwijderen
              </button>
            )}
            <button onClick={runAssist} disabled={aiLoading || locked}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: 'none', background: '#1e3a2f', color: '#fff', cursor: aiLoading || locked ? 'default' : 'pointer', opacity: aiLoading || locked ? 0.6 : 1, whiteSpace: 'nowrap' }}>
              {aiLoading ? 'AI ontwikkelt…' : "Concept-persona's genereren"}
            </button>
          </div>
        </div>
        {/* Bronnen die de AI meeneemt — transparant + stuurbaar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: `1px solid ${faseKleur(3).border}` }}>
          <span style={{ fontSize: 11.5, color: '#9aa1b0', fontWeight: 600 }}>Voeding:</span>
          {[
            { on: stakeholders.length > 0, label: 'Stakeholders', to: 'stakeholders' },
            { on: signals.gebied, label: 'Gebiedsanalyse', to: 'gebiedsanalyse' },
            { on: signals.media, label: 'Nieuws & sentiment', to: 'mediadashboard' },
          ].map((s) => (
            <button key={s.label} onClick={() => navigate(`/project/${project.id}/${s.to}`)}
              title={s.on ? 'Wordt meegenomen' : 'Nog niet ingevuld — klik om toe te voegen'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', border: '1px solid', borderColor: s.on ? '#cdebd6' : BRAND.rand, background: s.on ? '#f2fbf5' : '#fff', color: s.on ? '#1f7a44' : BRAND.groenMuted }}>
              <span>{s.on ? '✓' : '+'}</span>{s.label}
            </button>
          ))}
        </div>
        {(!signals.gebied || !signals.media) && (
          <div style={{ fontSize: 12, color: '#8a6a7a', marginTop: 8 }}>
            Tip: doe eerst de {[!signals.gebied && 'Gebiedskaart', !signals.media && 'Nieuws & sentiment'].filter(Boolean).join(' en ')} voor rijkere, actuelere persona's (leeftijd/inkomen uit demografie, zorgen uit het mediasentiment).
          </div>
        )}
      </div>

      {/* Samenvoegen: selecteer persona's die in de communicatie één doelgroep zijn */}
      {!locked && personas.length >= 2 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: selectMode ? 'space-between' : 'flex-end', gap: 10, flexWrap: 'wrap', marginBottom: 12, ...(selectMode ? { padding: '10px 14px', borderRadius: 10, background: BRAND.papier, border: `1px solid ${BRAND.rand}` } : {}) }}>
          {selectMode ? (
            <>
              <span style={{ fontSize: 13.5, color: BRAND.groen }}>
                Klik de persona's aan die één doelgroep worden. <strong>{selected.length} geselecteerd</strong>
              </span>
              <span style={{ display: 'flex', gap: 8 }}>
                <button onClick={stopSelect} style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}>Annuleren</button>
                <button onClick={startMerge} disabled={selected.length < 2}
                  style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', background: BRAND.groen, color: '#fff', cursor: selected.length < 2 ? 'default' : 'pointer', opacity: selected.length < 2 ? 0.5 : 1 }}>
                  Samenvoegen{selected.length >= 2 ? ` (${selected.length})` : ''}
                </button>
              </span>
            </>
          ) : (
            <button onClick={() => setSelectMode(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: `1px solid ${BRAND.rand}`, background: '#fff', color: BRAND.groen, cursor: 'pointer' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="18" r="3" /><circle cx="6" cy="6" r="3" /><path d="M6 21V9a9 9 0 0 0 9 9" /></svg>
              Persona's samenvoegen
            </button>
          )}
        </div>
      )}

      {/* Grid van persona-tegels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
        {personas.map((p) => {
          const partIdx = PARTICIPATIE.indexOf(p.participatieniveau);
          const src = p.afbeelding || deriveAvatar(p);
          const isSel = selectMode && selected.includes(p.id);
          return (
            <div key={p.id} onClick={() => (selectMode ? toggleSelect(p.id) : openEdit(p.id))}
              role={selectMode ? 'checkbox' : undefined} aria-checked={selectMode ? isSel : undefined}
              style={{ border: '1px solid #e5e7eb', borderRadius: 14, background: '#fff', overflow: 'hidden', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'box-shadow .15s, transform .15s', outline: isSel ? `3px solid ${BRAND.groen}` : 'none', outlineOffset: -1 }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 22px rgba(0,0,0,0.10)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'none'; }}
            >
              <div style={{ position: 'relative', height: 150, background: '#eef1f6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {src
                  ? <img src={src} alt={p.naam || 'Persona'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span aria-hidden="true" style={{ fontSize: 40, fontWeight: 700, color: '#9aa1b0', letterSpacing: 1 }}>{initials(p.naam)}</span>}
                {selectMode && (
                  <span aria-hidden="true" style={{ position: 'absolute', top: 8, left: 8, width: 26, height: 26, borderRadius: '50%', background: isSel ? BRAND.groen : 'rgba(255,255,255,0.92)', border: isSel ? 'none' : '2px solid #c7cbd6', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.18)', boxSizing: 'border-box' }}>
                    {isSel && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                  </span>
                )}
                {!locked && !selectMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); removePersona(p.id); }}
                    title="Persona verwijderen"
                    aria-label="Persona verwijderen"
                    style={{ position: 'absolute', top: 8, right: 8, width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.92)', color: '#b91c1c', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.18)' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                  </button>
                )}
              </div>
              <div style={{ padding: '14px 16px' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.naam || 'Naamloze persona'}</div>
                {p.groep && <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.groep}</div>}
                {p.gebaseerdOp && <div title={`Gebaseerd op: ${splitNamen(p.gebaseerdOp).join(', ')}`} style={{ fontSize: 11, color: BRAND.groenMuted, marginTop: 6, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Gebaseerd op: {splitNamen(p.gebaseerdOp).join(', ')}</div>}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                  {p.participatieniveau && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: PARTICIPATIE_TEXT[partIdx] || BRAND.groen, background: PARTICIPATIE_COLORS[partIdx] || BRAND.tint, borderRadius: 6, padding: '3px 8px' }}>{p.participatieniveau}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Nieuwe persona-tegel */}
        {!locked && !selectMode && (
          <button onClick={openCreate}
            style={{ border: '2px dashed #c3c9d4', borderRadius: 14, background: '#fbfcfd', color: '#5b6472', cursor: 'pointer', minHeight: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 14, fontWeight: 600 }}>
            <span style={{ display: 'inline-flex', width: 46, height: 46, borderRadius: '50%', background: '#eef1ff', color: '#33417a', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            </span>
            Nieuwe persona
          </button>
        )}
      </div>

      {personas.length === 0 && locked && (
        <div style={{ padding: 24, textAlign: 'center', color: '#8a90a0', fontSize: 14 }}>Geen persona's vastgelegd.</div>
      )}

      {!locked && (
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={saveDraft} disabled={saving} style={{ padding: '9px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Opslaan…' : 'Concept opslaan'}
          </button>
        </div>
      )}

      <LockBar status={status} onLock={doLock} onUnlock={doUnlock} lockedAt={lockedAt} canLock={canLock} busy={locking} faseLabel="Persona's" canClear={canClear} onClear={doClear} clearing={clearing} />
      {!canLock && !locked && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: '#8a90a0', textAlign: 'right' }}>
          Voeg minimaal één persona met een naam toe om te kunnen valideren.
        </div>
      )}
    </div>
  );
};
